<template>
  <g
    :class="{'event-wrapper': !isSelectedEvent(node.id)}"
  >
    <mask :id="node?.svgMaskPrefix ? `${node.svgMaskPrefix}-${node.id}` : `rmask-${node.id}`">
      <rect :x="node.x" :y="node.y" :width="104" :height="node?.withoutActions ? 80 : 96" fill="black"/>
      <rect :x="node.x" :y="node.y" :width="104" :height="node?.withoutActions ? 80 : 96" rx="4" fill="white"/>
    </mask>

    <!-- extra wrapper for filter to work around safari bug -->
    <g :filter="isSelectedEvent(node.id) ? '' : 'url(#shadow-event)'" class="event-group">
      <rect
        class="event"
        :id="'r-' + node.id"
        :x="node.x"
        :y="node.y"
        :mask="node?.svgMaskPrefix ? `url(#${node.svgMaskPrefix}-${node.id})` : `url(#rmask-${node.id})`"
        width="104"
        :height="node?.withoutActions ? 80 : 96"
        rx="4"
        :class="[{ 'connection-target': connectionTarget }, colorName ? `theme-fill-${colorName}` : '']"
        :style="{
          'stroke-width': isSelectedEvent(node.id) ? '2px' : node.outlined ? '1px' : '0px',
          fill: isSelectedEvent(node.id) ? lightenColor(color) : (!colorName ? color : undefined),
          stroke: isSelectedEvent(node.id) ? darkenColor(color) : (!colorName ? color : undefined),
          'fill-opacity': (!isSelectedEvent(node.id) && node.outlined) ? '0' : undefined
        }"
      >
      </rect>
    </g>
    <defs>
      <filter
        id="shadow-event"
        x="-20%"
        y="-20%"
        width="140%"
        height="140%"
        filterUnits="userSpaceOnUse"
        color-interpolation-filters="sRGB"
      >
        <feFlood flood-opacity="0" result="BackgroundImageFix"/>
        <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
        <feOffset dy="8"/>
        <feGaussianBlur stdDeviation="5.5"/>
        <feComposite in2="hardAlpha" operator="out"/>
        <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.1 0"/>
        <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_631_3776"/>
        <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
        <feOffset dy="2"/>
        <feGaussianBlur stdDeviation="2"/>
        <feComposite in2="hardAlpha" operator="out"/>
        <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.13 0"/>
        <feBlend mode="normal" in2="effect1_dropShadow_631_3776" result="effect2_dropShadow_631_3776"/>
        <feBlend mode="normal" in="SourceGraphic" in2="effect2_dropShadow_631_3776" result="shape"/>
      </filter>

      <filter id="shadow-event-hover" x="-50%" y="-50%" width="200%" height="200%">
        <!-- First shadow (0px 3px 6px rgba(0, 0, 0, 0.20)) -->
        <feOffset dx="0" dy="3" result="offset1" />
        <feGaussianBlur stdDeviation="3" result="blur1" />
        <feFlood flood-color="rgba(0, 0, 0, 0.20)" />
        <feComposite in2="blur1" operator="in" result="shadow1" />

        <!-- Second shadow (0px 6px 16px rgba(0, 0, 0, 0.12)) -->
        <feOffset dx="0" dy="6" result="offset2" />
        <feGaussianBlur stdDeviation="8" result="blur2" />
        <feFlood flood-color="rgba(0, 0, 0, 0.12)" />
        <feComposite in2="blur2" operator="in" result="shadow2" />

        <!-- Combine shadows -->
        <feMerge>
          <feMergeNode in="shadow1" />
          <feMergeNode in="shadow2" />
          <feMergeNode in="SourceGraphic" />
        </feMerge>
      </filter>
    </defs>

    <!-- editable Event Label -->
    <foreignObject
      v-if="showOnCardConditionLabel(node)"
      :x="node.x - 20"
      :y="node.y - 28"
      width="160"
      height="32"
    >
      <bpmn-event-label
        :node="node"
        :condition-label="getConnectionLabel(node, onCardGatewayParentId(node))"
        :auto-focus-on-select="true"
        @label-blur="blurOnConditionLabels($event, node, onCardGatewayParentId(node))"
        @label-focus="focusOnConditionLabel"
      ></bpmn-event-label>
    </foreignObject>

    <!-- editable Event name -->
    <foreignObject
      :x="node.x + 5"
      :y="node.y"
      width="94"
      height="80"
    >
      <div
        @click.prevent="onEventClick"
        @mousedown="onEventMousedown"
        class="text-box justify-center align-center"
        @dragover.prevent="eventDragoverHandler"
        @drop.prevent="eventDropHandler"
      >
        <div
          ref="eventName"
          :id="node.id"
          :style="isSelectedEvent(node.id) ? { color: '#000000' } : {}"
          class="text-cell event-name align-center justify-center draggable"
          :class="colorName ? `theme-text-${colorName}` : ''"
          :data-test="node?.withoutActions ? undefined : svgGraphData?.[node.id]?.name"
          :data-test-version="svgGraphData?.[node.id]?.version"
          :contenteditable="!$store.state.isWorkflowDisabled && node.id === $route.params.eventId && selectedEventIds.length <= 1"
          @keydown.stop.exact="checkCharacterLimit($event, 50)"
          @keydown.shift.exact="checkCharacterLimit($event, 50)"
          @keydown.stop.prevent.enter="toggleFocus"
          @keydown.stop.esc="restoreValue($event, node.name)"
          @keydown.stop.tab.exact="moveRight($event)"
          @keydown.stop.tab="moveLeft($event)"
          @blur.stop="blurOnDescription($event, node)"
          @paste="onPaste($event, $refs.eventName, 50)"
          v-text="node.name || node.description"
          @input="() => updateFontSizeDynamically($refs.eventName)"
        ></div>
      </div>
    </foreignObject>
  </g>
</template>

<script>
import BpmnEventLabel from '@/components/BpmnEventLabel'
import { diagramHelpers } from '@/mixins/diagramHelpers.js'
import workflowApi from '@/api/workflowApi'
import { updateFontSizeDynamically } from '@/utils/update-font.js'
import { darkenColor, lightenColor, getDarkThemeColor, getColorName } from '@/utils/color'
import { isDarkTheme } from '@/composables/useAppTheme'
import { mapGetters, mapState } from 'vuex'

export default {
  components: {
    BpmnEventLabel
  },

  mixins: [diagramHelpers],

  props: {
    connectionTarget: Boolean,
    disabled: Boolean,
    node: Object,
    svgGraph: Object,
    svgLanes: Object,
    eventDefinition: Object,
    workflow: Object,
    svgGroups: Object,
    selectedEventIds: {
      type: Array,
      default: () => []
    }
  },

  methods: {
    updateFontSizeDynamically,
    darkenColor,
    lightenColor,
    onCardGatewayParentId (node) {
      return node.parents?.find(
        (parentId) => this.svgGraph[parentId]?.type === 'bpmn:ExclusiveGateway'
      )
    },
    showOnCardConditionLabel (node) {
      if (!node.showConditionLabelOnEvent) {
        return false
      }
      const gatewayParentId = this.onCardGatewayParentId(node)
      if (!gatewayParentId) {
        return false
      }
      const xDistance = node.x - this.svgGraph[gatewayParentId].x

      return xDistance > 0 && xDistance <= 160
    },
    moveRight (event) {
      this.$emit('move-right')
      event.target.blur()
      event.preventDefault()
    },
    moveLeft (event) {
      this.$emit('move-left')
      event.target.blur()
      event.preventDefault()
    },
    onEventMousedown (event) {
      // Prevent mousedown propagation on shift-click to avoid triggering box selection
      if (event.shiftKey) {
        event.stopPropagation()
      }
    },
    onEventClick (event) {
      if (this.connectionTarget) {
        this.addConnection(this.node.id)
      } else {
        // Check if shift key is pressed for multi-selection
        if (event.shiftKey) {
          event.preventDefault()
          // Call parent's shift-click handler
          this.$parent.handleShiftClickSelection(this.node.id)
        } else {
          // Don't clear selection if we just finished dragging
          if (this.$parent?.justFinishedDragging) return

          // Clear any multi-selection and select only this event
          this.$parent?.clearMultiSelection?.()
          this.changeSelectedEvent(this.node.id)
          if (this.$route.params.eventId !== this.node.id) {
            event.preventDefault()
          }
        }
        // var div = document.getElementById(this.node.id)
        // setTimeout(function () {
        //   div.focus()
        // }, 0)
      }
    },

    eventDragoverHandler (ev) {
      ev.dataTransfer.dropEffect = 'copy'
    },

    eventDropHandler (ev) {
      if (ev.dataTransfer.getData('component') === 'user-story-mapping') {
        const data = ev.dataTransfer.getData('text')
        workflowApi.moveCard(this.$route.params.id, '', ev.target.id, data)
      } else {
        const cardTypeId = ev.dataTransfer.getData('text/plain')
        this.addCardToEvent(this.node.id, cardTypeId, '<p>New card</p>')
      }
    }
  },

  mounted () {
    if (this.$refs.eventName) {
      updateFontSizeDynamically(this.$refs.eventName)
    }
    if (this.node.selectEventText) {
      // yuriy
      // for some reason `this.$nextTick` doesn't fix the problem
      // with async state change of the component
      // `setTimeout` does a trick and allows to select the text
      // when element becomes selected
      //
      // staffan:
      // disabled because of cypress tests
      // window.setTimeout(() => {
      //   const node = this.$el.querySelector('.event-name')
      //   if (node) {
      //     const selection = window.getSelection()
      //     const range = document.createRange()
      //     range.selectNodeContents(node)
      //     selection.removeAllRanges()
      //     selection.addRange(range)
      //   }
      // }, 1000)
    }
  },

  computed: {
    ...mapGetters({
      baseData: 'svgGraphData/baseData',
      svgGraphData: 'svgGraphData/svgGraphData'
    }),
    ...mapState({
      cardTypes: state => state.cardTypes.cardTypes
    }),
    color () {
      const rgb = this.node.color ? this.node.color.rgb : this.eventDefinition.color.rgb
      return isDarkTheme() ? getDarkThemeColor(rgb) : rgb
    },
    colorName () {
      const rgb = this.node.color ? this.node.color.rgb : this.eventDefinition.color.rgb
      return getColorName(rgb)
    }
  }
}
</script>

<style scoped>
.event-wrapper:hover .event-group {
  filter: url(#shadow-event-hover);
}

.event {
  stroke: black;
  stroke-width: 2px;
}

.v-tooltip__content {
  background: white;
  color: var(--color-primary);
  border-radius: 10px;
}

.v-tooltip__content code {
  background: var(--color-primary);
  color: white;
}

.connection-target {
  animation: target 0.8s ease-in-out both infinite;
}

@keyframes target {
  0% {
    stroke-width: 2px;
    stroke: black;
  }

  50% {
    stroke-width: 5px;
  }

  100% {
    stroke-width: 2px;
    stroke: black;
  }
}

.event-name {
  color: var(--text-dark);
  font-weight: 500;
  word-break: break-word;
  height: 80px;
  max-height: 80px;
  min-height: 80px;
  width: 100%;
  text-align: center;
  display: flex;
}

.event-name:focus-visible {
  outline: none;
}

.text-box {
  display: flex;
  height: 100%;
  width: 100%;
  overflow: hidden;
  line-height: 1.3;
}

/* .shadow {
  -webkit-filter: drop-shadow(3px 3px 2px rgba(0, 0, 0, 0.7));
  filter: drop-shadow(3px 3px 2px rgba(0, 0, 0, 0.7));
} */
</style>

<style>
.text-box {
  display: flex;
  height: 100%;
  width: 100%;
  overflow: hidden;
  line-height: 1.3;
}

.event-name {
  color: var(--text-dark);
  font-weight: 500;
  word-break: break-word;
  height: 80px;
  max-height: 80px;
  min-height: 80px;
  width: 100%;
  text-align: center;
  display: flex;
}
</style>
